import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Profile } from "../../../types/types";

interface ManageFightersProps {
  member: Profile;
  onAddFightersClick?: () => void;
  isOwnProfile?: boolean;
}

const ManageFighters: React.FC<ManageFightersProps> = ({
  member,
  onAddFightersClick,
  isOwnProfile = false,
}) => {
  const { t } = useTranslation("publicProfile");
  const navigate = useNavigate();

  return (
    <div className="space-y-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">
          {t("linked_fighters")}
        </h3>
        {isOwnProfile && onAddFightersClick && (
          <button
            type="button"
            onClick={onAddFightersClick}
            className="h-8 px-3 rounded-2xl bg-blue-600 text-white hover:bg-blue-700 text-xs font-medium inline-flex items-center gap-1.5 transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4" />
            {t("add_new_fighters")}
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
        {member.fighterManagerLinkResponseDtos &&
        member.fighterManagerLinkResponseDtos.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-100 text-sm text-start">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500">
                  {t("fighter")}
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500">
                  {t("status")}
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500">
                  {t("assigned_at")}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 bg-white">
              {member.fighterManagerLinkResponseDtos.map((link) => (
                <tr
                  key={link.id}
                  className="hover:bg-gray-50/70 transition-colors cursor-pointer"
                  onClick={() =>
                    navigate(`/view-profile?profileId=${link.profile.id}`)
                  }
                >
                  <td className="px-5 py-3.5 align-middle">
                    <div className="flex items-center gap-3">
                      {link.profile.profileImage ? (
                        <img
                          src={link.profile.profileImage}
                          alt={`${link.profile.firstName} ${link.profile.lastName}`}
                          className="w-9 h-9 rounded-xl object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center font-medium text-xs text-gray-600">
                          {link.profile.firstName?.[0]}
                          {link.profile.lastName?.[0]}
                        </div>
                      )}
                      <span className="font-medium text-gray-900">
                        {link.profile.firstName} {link.profile.lastName}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 align-middle">
                    {link.active ? (
                      <span className="inline-flex items-center rounded-full bg-green-50 border border-green-200 px-2.5 py-0.5 text-xs text-green-700 font-medium">
                        Accepted
                      </span>
                    ) : link.removedAt ? (
                      <span className="inline-flex items-center rounded-full bg-red-50 border border-red-200 px-2.5 py-0.5 text-xs text-red-600 font-medium">
                        Declined
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs text-amber-700 font-medium">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-xs text-gray-500 align-middle">
                    {new Date(link.assignedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-xs text-gray-400">
            {t("no_fighters_linked")}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageFighters;
